// @givmo/app-charity — an invitation's two facts, carried to the create form (CBG-SEC2; contract §4.2).
//
// "Continue with email" from an invitation opens the console's create form in its director arrival,
// whose lines name the board ("Then you'll join the … board") and where the invitation went ("Your
// invitation went to d•••@example.org"). Carried like the email handoff (`../auth/email-handoff.ts`)
// and for its reasons: per tab, consumed on read, never in the URL (the masked address is still the
// seat's). Every access is inside a try: a storage fault costs the two lines, never the form.
//
// CBG-SEC2-L4-MOVE (frame b3c): a director moving their seat to a new account carries the same two
// facts for the move's own two lines ("Then your seat on the … board moves to it.", "Your seat’s
// address is …"), marked `seat_move`. Never the move's ticket, which stays in Board's tab.

const KEY = 'givmo-charity-director-invitation';

export interface DirectorInvitationHandoff {
  organizationName: string | null;
  maskedEmailOfRecord: string;
  /** What the new account is for: joining from an invitation (the default), or a seat's move. */
  purpose?: 'invitation' | 'seat_move';
}

/** Stash the invitation's two facts for the create form. */
export function stashDirectorInvitation(handoff: DirectorInvitationHandoff): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(handoff));
  } catch {
    // Storage unavailable — the form shows its plain lines.
  }
}

/** Take (and clear) the stashed facts, or null when there are none, they're malformed, or storage is unavailable. */
export function takeDirectorInvitation(): DirectorInvitationHandoff | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { organizationName, maskedEmailOfRecord, purpose } = parsed as Record<string, unknown>;
    if (typeof maskedEmailOfRecord !== 'string' || maskedEmailOfRecord.length === 0) return null;
    return {
      organizationName:
        typeof organizationName === 'string' && organizationName.trim().length > 0
          ? organizationName.trim()
          : null,
      maskedEmailOfRecord,
      purpose: purpose === 'seat_move' ? 'seat_move' : 'invitation',
    };
  } catch {
    return null;
  }
}
